import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { ConfigStateContext } from "../../../../Contexts/Configurations/context";
import { LeadButtons } from "../LeadButtons/web";
import cookie from "js-cookie";
import { isValidCharactersXSS, isValidEmail, getBaseDomain as loginBasePath } from "@Utils/Functions";
import { useContact } from "../hook";
import { useFacebookPixel } from "../../../../GlobalHooks/web/FacebookPixel.hook";
import { useRouter } from "next/router";
import { useUser } from "../../../User/User.hook";
import getConfig from "next/config";
import { ModalState, ModalStatus, TextFormProps } from "./type";
import "./styles.less";
import CheckboxCustomInput from "shared-components/Components/CheckboxCustomInput";
import { SimilarsLeadsModal } from "../SimilarLeadsModal/SimilarLeadsModal";
import { CountriesDropdown } from "shared-components/Components/Dropdowns/CountriesDropdown/web";
import { CountryCode, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberWithError } from "libphonenumber-js";
import { phoneStartWithIndicative } from "@Utils/phones";
import { MessageIcon } from "shared-components/Components/CustomIcons/MessageIcon/web";
import { LoadingIcon } from "shared-components/Components/CustomIcons/LoadingIcon/LoadingIcon";
import { CodePhoneType, useContactModal } from "shared-components/Contexts/ContactModal/context";
import { DollarOutlined } from "shared-components/Components/CustomIcons/DollarOutlined/web";
import { PhoneOutlined } from "shared-components/Components/CustomIcons/PhoneOutlined/web";
import { CalendarOutlined } from "shared-components/Components/CustomIcons/CalendarOutlined/web";
import { useTechnicalSheet } from "../../TechnicalSheet/hook";
import { getInitialContactForm } from "src/utils/Functions";
import { CheckCircleTwoTone } from "@ant-design/icons";
import { Modal } from "shared-components/Components/Modal/web";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { WarningIcon } from "shared-components/Components/CustomIcons/WarningIcon/web";

const { NODE_ENV } = getConfig().publicRuntimeConfig;

export const TextForm = ({
	id,
	onComplete = null,
	disabled = false,
	defaultMessage = "",
	isInListing = false,
	queryRouter,
	isProjectSpecialPage = false,
	slug = "",
	isProject = false,
	owner = null,
	isFromModal = false,
	autoFocus = true,
	showTextInput = true,
	expressCallback = null,
	source = "",
	propertyLink = undefined,
	ctaToShow,
	isExpress = false
}: TextFormProps) => {
	const Router = useRouter();
	const {
		isLoggedIn,
		user,
	} = useUser();
	const { country_code, site_name } = useContext(ConfigStateContext);
	const { sendLead } = useTechnicalSheet({ id });
	const screen = useBreakpoint();
	const bell = useRef(null);
	const [modalState, setModalState] = useState<ModalStatus>({
		similarModal: ModalState.DEFAULT,
		contactLead: ModalState.DEFAULT,
	});
	const [isLoading, setIsLoading] = useState(false);
	const [contactWarning, setContactWarning] = useState<"phone" | "whatsapp" | null>(null);
	const [formType, setFormType] = useState<number>(0);
	const [modalSuccessVisible, setModalSuccessVisible] = useState(false);
	const [isSendedConsultation, setIsSendedConsultation] = useState({
		lowerPrice: false,
		wantedCall: false,
		scheduleVisit: false
	});
	const nameRef = useRef<HTMLInputElement>(null);
	const phoneRef = useRef<HTMLInputElement>(null);
	const emailRef = useRef<HTMLInputElement>(null);
	const messageRef = useRef<HTMLTextAreaElement>(null);
	const contactText = isExpress ? "Enviar consulta" : "Contactar";
	const defaultMessageValue = !!defaultMessage.length ? defaultMessage : `Hola, vi esta propiedad en ${site_name} y me interesa recibir más información. ¡Muchas gracias!`;
	const openModal = (type: number = 0) => {
		setFormType(type);
		setModalState(prevState => ({ ...prevState, similarModal: ModalState.OPEN }));
	};
	const {
		sendContact,
		resetForm,
		error,
		formCompleted,
		disabledEmail,
		dataRegisterMutation,
		dataUserExist,
		doContactMutation,
	} = useContact({
		id,
		onComplete: openModal,
		type: "lead",
		isProject,
		source: isInListing ? 22 : screen.lg ? 0 : 4,
		utm: Router.query,
		env: NODE_ENV == "development" || NODE_ENV == "beta" ? "development" : "production",
	});
	const {
		modalData,
		ctaButtons,
		openContactModal,
		closeContactModal,
		formData,
		setFormData,
		errors,
		setErrors,
		selectedCountryCode,
		setSelectedCountryCode
	} = useContactModal();
	const isMultiCtaValue = ctaButtons?.contact && ctaButtons?.phone && ctaButtons?.whatsapp;
	const isFincaraizColombia = process.env.APP_THEME === "fincaraiz" && country_code === "CO";

	const handleClickShowNotification = async ({
		type = "success",
		message = "",
		description = "",
	}) => {
		const { showNotification } = await import(
			"shared-components/Components/CustomNotification/web"
		);
		showNotification(type, message, description);
	};

	// Simula el click de CTA "Avisame cuando baje de precio"
	useEffect(() => {
		setTimeout(() => {
			if (queryRouter && queryRouter === "bell") {
				if (bell.current) bell.current.click();
			} else if (queryRouter && queryRouter === "mailConsulta") {
				doContactMutation(99); //numero random para que envie el mensaje default en backend
			}
		}, 3000);
	}, [queryRouter]);

	const FP = useFacebookPixel();
	const userData = useMemo(() => user.data?.me, [user.data?.me]);
	const initialFormData = useMemo(() => ({
        ...getInitialContactForm(isLoggedIn, user?.data),
        message: defaultMessageValue,
    }), [isLoggedIn, user?.data, defaultMessageValue]);

	const reset = () => {
		resetForm();

		if (isLoggedIn) {
			setFormData({
				name: user.data?.me?.name ?? "",
				phone: formatPhoneNumber(user.data?.me?.phone ?? ""),
				email: user.data?.me?.email ?? "",
				busca_por_ti: false,
				message: defaultMessage,
				fincatyc: false
			});
		}

		setIsLoading(false);
	};

	const parseUserPhone = useCallback((phone) => {
        if (!phone) return { 
            nationalNumber: '', 
            countryCode: selectedCountryCode 
        };
        
        try {
            const parsedPhoneNumber = parsePhoneNumberWithError(phone);
            return {
                nationalNumber: parsedPhoneNumber?.nationalNumber || '',
                countryCode: {
                    code: parsedPhoneNumber?.country || selectedCountryCode.code,
                    value: `+${parsedPhoneNumber?.countryCallingCode || selectedCountryCode.value.replace('+', '')}`,
                }
            };
        } catch (error) {
            try {
                // Segundo intento con el código de país seleccionado
                const parsedPhoneNumber = parsePhoneNumberWithError(phone, selectedCountryCode?.code as CountryCode);
                const isValidNumber = isValidPhoneNumber(phone, selectedCountryCode?.code as CountryCode);
                
                if (isValidNumber) {
                    return {
                        nationalNumber: parsedPhoneNumber?.nationalNumber || '',
                        countryCode: {
                            code: parsedPhoneNumber?.country || selectedCountryCode.code,
                            value: `+${parsedPhoneNumber?.countryCallingCode || selectedCountryCode.value.replace('+', '')}`,
                        }
                    };
                }
            } catch (secondError) {
                console.error('Error parsing phone:', secondError);
            }
            
            // Si todo falla, devolver el teléfono original
            return { 
                nationalNumber: phone, 
                countryCode: selectedCountryCode 
            };
        }
    }, [selectedCountryCode]);

	const processedFormData = useMemo(() => {
        if (!userData || !isLoggedIn) {
            return initialFormData;
        }

        const phone = userData.phone || formData.phone || '';
        const { nationalNumber, countryCode } = parseUserPhone(phone);
        
        return {
            ...formData,
            name: userData.name || '',
            phone: nationalNumber,
            email: userData.email || ''
        };
    }, [userData, isLoggedIn, initialFormData, parseUserPhone, formData.phone]);

	useEffect(() => {
        // Compara solo si es necesario actualizar
        const needsUpdate = (
            processedFormData.name !== formData.name ||
            processedFormData.phone !== formData.phone ||
            processedFormData.email !== formData.email ||
            (!userData && formData.message !== defaultMessageValue)
        );

        if (needsUpdate) {
            setFormData(processedFormData);
            
            // Solo ejecutar focusEmptyData si es desde modal y autoFocus es true
            if (isFromModal && autoFocus) {
                // Usar setTimeout para evitar conflictos con el render y eliminamos el bucle
                setTimeout(() => focusEmptyData(processedFormData), 0);
            }
        }

        // Actualizar el código de país si es necesario
        if (userData?.phone && isLoggedIn) {
            const { countryCode } = parseUserPhone(userData.phone);
            if (countryCode.code !== selectedCountryCode.code) {
                setSelectedCountryCode(countryCode);
            }
        }
    }, [
        userData?.name,
        userData?.phone, 
        userData?.email,
        isLoggedIn,
        isFromModal,
        autoFocus
        // Nota: NO incluir formData aquí para evitar loops
    ]);

	useEffect(() => {
		Router.events.on("routeChangeStart", reset);

		return () => Router.events.on("routeChangeStart", reset);
	}, []);

	useEffect(() => {
        if (!isLoggedIn && !userData) {
            const shouldUpdate = (
                formData.name !== initialFormData.name ||
                formData.email !== initialFormData.email ||
                formData.message !== initialFormData.message
            );

            if (shouldUpdate) {
                setFormData(initialFormData);
                if (isFromModal && autoFocus) {
                    setTimeout(() => focusEmptyData(initialFormData), 0);
                }
            }
        }
    }, [isLoggedIn, userData]);

	const formatPhoneNumber = (phone: string) => {
		try {
			const parsedPhoneNumber = parsePhoneNumberWithError(phone);
			return parsedPhoneNumber?.nationalNumber;
		} catch (e) {
			return phone;
		}
	};

	const focusEmptyData = (data) => {
		if (!data?.name?.length && nameRef?.current) {
			nameRef.current.focus();
		} else if (!data?.phone?.length && phoneRef?.current) {
			phoneRef.current.focus();
		} else if (!data?.email?.length && emailRef?.current) {
			emailRef?.current?.focus();
		} else if (!data?.message?.length && messageRef?.current) {
			messageRef?.current?.focus();
		}
	}

	// Aqui capturo los errores de la query de userExistsQuery
	useEffect(() => {
		if (error && error.message.includes("ya se encuentra registrada")) {
			handleClickShowNotification({
				message: error.message,
				type: "error",
			});

			setIsLoading(false);
		}
	}, [error]);

	useEffect(() => {
		if (formCompleted) {
			FP.StandardEvent("CompleteRegistration");
		}
	}, [formCompleted]);

	useEffect(() => {
		if (dataRegisterMutation) {
			refreshAuth(dataRegisterMutation.registerIndividual);
		}
	}, [dataRegisterMutation]);

	useEffect(() => {
		if (dataUserExist) {
			const leadWithoutLogin = true;
			doContactMutation(0, leadWithoutLogin);
		}
	}, [dataUserExist]);
	
	useEffect(() => {
		if (!!selectedCountryCode.code.length) {
			const phoneIsValid = !!formData.phone?.length && 
				isValidPhoneNumber(formData.phone, selectedCountryCode?.code as CountryCode) || 
				phoneStartWithIndicative(selectedCountryCode?.value, formData?.phone) ||
				!formData.phone?.length;
			const emailIsValid = !!formData.email?.length && isValidEmail(formData.email) || !formData.email?.length;

			setErrors({ 
				...errors,
				phone: !phoneIsValid,
				email: !emailIsValid
			});
		}
	}, [formData.phone, formData.email, selectedCountryCode.code]);

	const refreshAuth = data => {
		cookie.set("frontend_token", data.access_token, {
			expires: 365,
			domain: loginBasePath(),
			path: "/",
		});
		localStorage.setItem("user_md5", data.user_md5);
		user.refetch();
	};

	const validateContactForm = (cta?: "phone" | "whatsapp") => {
		const termsAccepted = !isFincaraizColombia || user?.data?.me?.is_terms_and_conditions_accepted || formData.fincatyc;

		if (isFormFilled() && termsAccepted) {
			setContactWarning(null);
			setErrors({
				name: false,
				phone: false,
				email: false,
				fincatyc: false,
			});

			return true;
		}

		const newErrors = {
			name: false,
			phone: false,
			email: false,
			fincatyc: false,
		}
		if (isFincaraizColombia) {
			if (formData.name.trim() === "" || !isValidCharactersXSS(formData.name)) newErrors.name = true;
			if (formData.email.trim() === "" || !isValidEmail(formData.email)) newErrors.email = true;
		} else {
			if (formData.name === "") newErrors.name = true;
			if (formData.email === "") newErrors.email = true;
		}
		if (
			formData.phone === "" ||
			!isValidPhoneNumber(formData?.phone, selectedCountryCode?.code as CountryCode) ||
			phoneStartWithIndicative(selectedCountryCode?.value, formData?.phone)
		) {
			newErrors.phone = true;
		}
		if (isFincaraizColombia && !user?.data?.me?.is_terms_and_conditions_accepted && !formData.fincatyc) newErrors.fincatyc = true;

		setErrors(newErrors);
		setContactWarning(isFincaraizColombia ? cta ?? null : null);

		return false;
	};

	const submitForm = (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (validateContactForm()) {
			setIsLoading(true);

			if (expressCallback) expressCallback(event);
			else {
				const extraData = {
					isSendingConsultation: true
				}

				setFormData(prev => ({
					...prev,
					...extraData,
					name: prev.name?.trim(),
					message: prev.message?.trim()
				}));

				sendContact({
					...formData,
					...extraData,
					phone: `${selectedCountryCode?.value}${formData.phone}`,
				});

				if (isProjectSpecialPage && slug && id) {
					let host = window.location.host;
					let TEST_HOST = "xn--4v8h.infocasas.com.uy";
					if (host === TEST_HOST || host === "localhost:3000") {
						host = "beta.infocasas.com.uy";
					}
					let reqEndpoint = `https://${host}/?mid=proyectos&func=landingDinamica&slug=${slug}&formulario&IDproyecto=${id}&json&nl`;
					let body = {
						nombre: formData.name,
						email: formData.email,
						tel: `${selectedCountryCode?.value}${formData.phone}`,
						extra: formData.message,
						extra_completo: `${formData.message}.slug:${slug}`,
						InfoLeads: 1,
					};

					fetch(reqEndpoint, {
						method: "POST",
						body: JSON.stringify(body),
					}).then(res => {
						console.log(res);
					});
				}
			};

		}
	};

	const onCancelSimilarsLead = () => {
		setModalState(prevState => ({
			...prevState,
			similarModal: ModalState.CLOSE,
			contactLead: ModalState.CLOSE,
		}));
		setIsLoading(false);
		if (onComplete) onComplete();
	};

	const handleBlur = (
		e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
		fieldName: string
	) => {
		const value = e.target.value;
		localStorage.setItem("contactForm", JSON.stringify(formData));
		localStorage.setItem("selectedCountryCode", JSON.stringify(selectedCountryCode));

		if (fieldName === "name") {
			const isValid = value.trim() !== "" && isValidCharactersXSS(value);
			setErrors({ ...errors, name: !isValid });
		} else if (fieldName === "phone") {
			setErrors({
				...errors,
				phone:
					!isValidPhoneNumber(String(value), selectedCountryCode?.code as CountryCode) ||
					phoneStartWithIndicative(selectedCountryCode?.value, formData?.phone),
			});
		} else if (fieldName === "email") {
			const isValid = value.trim() !== "" && isValidEmail(value);
			setErrors({ ...errors, email: !isValid });
		} else if (fieldName === "message") {
			setErrors({ ...errors });
		}
	};

	const isFormFilled = () => {
		if (!isFincaraizColombia) {
			return (
				formData.name !== "" &&
				isValidCharactersXSS(formData?.phone) &&
				formData.email !== "" &&
				isValidEmail(formData?.email) &&
				formData.phone !== "" &&
				isValidPhoneNumber(formData?.phone, selectedCountryCode?.code as CountryCode) &&
				!phoneStartWithIndicative(selectedCountryCode?.value, formData?.phone)
			);
		}

		if (
			formData.name.trim() !== "" &&
			isValidCharactersXSS(formData?.name) &&
			formData.email !== "" &&
			isValidEmail(formData?.email) &&
			formData.phone !== "" &&
			isValidPhoneNumber(formData?.phone, selectedCountryCode?.code as CountryCode) &&
			!phoneStartWithIndicative(selectedCountryCode?.value, formData?.phone)
		) return true;
		else return false;
	};

	const handleLeadButtonValidation = (e, cta: "phone" | "whatsapp") => {
		if (!isFincaraizColombia) return true;
		const isValid = validateContactForm(cta);

		if (!isValid) {
			e.preventDefault();
			e.stopPropagation();
		}

		return isValid;
	};

	const handleCountryChange = (value: {code:string, icon:string, key:string, text:string, value:string}) => {
		const payload: CodePhoneType = {
			value: value.value,
			code: value.code,
		}
		setSelectedCountryCode(payload);
		setContactWarning(null);
		setFormData({ ...formData, phone: "" });
		handleBlur({ target: { value: formData?.phone } }, "phone");
		localStorage.setItem("selectedCountryCode", JSON.stringify(selectedCountryCode));
	};

	const handleFincaTYC = (e: React.ChangeEvent<HTMLInputElement>) => {
		setContactWarning(null);
		setFormData({ ...formData, fincatyc: e.target.checked });
		setErrors({ ...errors, fincatyc: false });
	};

	const handleClick = async (e: MouseEvent, type: number) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		
		const dataModal = getInitialContactForm(isLoggedIn, user.data);
		
		if (!screen.lg) {
			if (dataModal.name === "") {
				setErrors({ ...errors, name: true });
				return;
			}
			if (dataModal.email === "") {
				setErrors({ ...errors, email: true });
				return;
			}
			if (
				dataModal.phone === "" ||
				!isValidPhoneNumber(dataModal?.phone, selectedCountryCode?.code as CountryCode) ||
				phoneStartWithIndicative(selectedCountryCode?.value, dataModal?.phone)
			){
				setErrors({ ...errors, phone: true });
				return;
			}
		}

		let res = await sendLead(type.toString(), {
			name: dataModal.name,
			phone: selectedCountryCode.value + dataModal.phone,
			email: dataModal.email,
		});

		if (res) {
			setModalSuccessVisible(true);

			if (type === 1) setIsSendedConsultation({ ...isSendedConsultation, lowerPrice: true });
			else if (type === 2) setIsSendedConsultation({ ...isSendedConsultation, wantedCall: true });
			else if (type === 3) setIsSendedConsultation({ ...isSendedConsultation, scheduleVisit: true });
		}
	}

	const askFastAction = async type => {
		let res = await sendLead(type.toString());

		if (res) {
			setModalSuccessVisible(true);

			if (type === 1) setIsSendedConsultation({ ...isSendedConsultation, lowerPrice: true });
			else if (type === 2) setIsSendedConsultation({ ...isSendedConsultation, wantedCall: true });
			else if (type === 3) setIsSendedConsultation({ ...isSendedConsultation, scheduleVisit: true });
		}
	};

	const handleExpressClick = (type: number) => {
		if (!isLoggedIn) {
			if (screen.lg) {
				openContactModal({
					id: type,
					owner: null,
					isProject: false,
					projectId: null,
					defaultMessage: "",
					source: "propertyDetail",
					isExpress: true,
					expressCallback: (e) => handleClick(e, type),
					ctaToShow: {
						contact: true,
						phone: false,
						whatsapp: false
					}
				})
			} else {
				handleClick(null, type);
			}
		} else {
			askFastAction(type);
		}
	}

	const renderWarningMessage = (message: string, className = "error-message") => (
		isFincaraizColombia ? (
			<span className={`${className} ${className}--with-icon`}>
				<WarningIcon className={`${className}-icon`} />
				{message}
			</span>
		) : (
			<span className={className}>{message}</span>
		)
	);

	return (
		<>
			{modalState.similarModal == ModalState.OPEN && (
				<SimilarsLeadsModal
					id={id}
					onCancelSimilarsLead={onCancelSimilarsLead}
					onComplete={onComplete}
					setModalState={setModalState}
					reset={reset}
					selectedCountryCode={selectedCountryCode?.value}
					formType={formType}
					setFormType={setFormType}
					formData={formData}
					isInListing={isInListing}
					isProject={isProject}
				/>
			)}

			<Modal
				show={modalSuccessVisible}
				onClose={() => {
					setModalSuccessVisible(false);
					closeContactModal();
				}}
				width={"420px"}
				customIndex={1002}
			>
				<div className="success-container">
					<CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "25px" }} />
					<span className="text">¡Consulta express enviada con éxito!</span>
				</div>
			</Modal>

			<form className="contact-form">
				{isFincaraizColombia &&
					<p style={{ marginTop: ".5rem" }}>
						Completa tus datos para habilitar {isMultiCtaValue ? "los medios" : "el medio"} de contacto
					</p>
				}
				
				<div className="container-input">
					<input
						ref={nameRef}
						className={"contact-form-input " + (errors.name ? "error" : "")}
						name="nombre"
						placeholder="Nombre y Apellido*"
						disabled={disabled}
						value={formData.name}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setContactWarning(null);
							setFormData({ ...formData, name: e.target.value });
						}}
						onBlur={e => handleBlur(e, "name")}
						required
					/>
					{errors.name && renderWarningMessage("Nombre no válido")}
				</div>

				{isFincaraizColombia ? (
					<div className="container-input">
						<div className="row phone-row-with-error">
							<div
								className="container-input"
								style={{ width: "auto" }}>
								<CountriesDropdown
									onChange={handleCountryChange}
									countryCode={selectedCountryCode}
									disabled={disabled}
								/>
							</div>
							<div className="container-input" style={{ width: "100%" }}>
								<input
									ref={phoneRef}
									className={"contact-form-input " + (errors.phone ? "error" : "")}
									name="telefono"
									placeholder="Teléfono*"
									disabled={disabled}
									value={formData.phone}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const phone = e?.target?.value?.replace(/\D/g, "");
										setContactWarning(null);
										setFormData({
											...formData,
											phone,
										});
										handleBlur({ target: { value: phone } }, "phone");
									}}
									onBlur={e => {
										handleBlur(e, "phone")
									}}
									type="tel"
									required
								/>
							</div>
						</div>
						{errors.phone && renderWarningMessage("Teléfono no válido")}
					</div>
				) : (
					<div className="row">
						<div
							className="container-input"
							style={{ width: "auto" }}>
							<CountriesDropdown
								onChange={handleCountryChange}
								countryCode={selectedCountryCode}
								disabled={disabled}
							/>
						</div>
						<div className="container-input" style={{ width: "100%" }}>
							<input
								ref={phoneRef}
								className={"contact-form-input " + (errors.phone ? "error" : "")}
								name="telefono"
								placeholder="Teléfono*"
								disabled={disabled}
								value={formData.phone}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									const phone = e?.target?.value?.replace(/\D/g, "");
									setContactWarning(null);
									setFormData({
										...formData,
										phone,
									});
									handleBlur({ target: { value: phone } }, "phone");
								}}
								onBlur={e => {
									handleBlur(e, "phone")
								}}
								type="tel"
								required
							/>
							{errors.phone && <span className="error-message">Teléfono no válido</span>}
						</div>
					</div>
				)}

				<div className="container-input">
					<input
						ref={emailRef}
						className={"contact-form-input " + (errors.email ? "error" : "")}
						name="email"
						type="email"
						placeholder="Email*"
						disabled={disabledEmail || disabled}
						value={formData.email}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setContactWarning(null);
							setFormData({ ...formData, email: e.target.value });
						}}
						onBlur={e => handleBlur(e, "email")}
						required></input>
					{errors.email && renderWarningMessage("Email no válido")}
				</div>

				{showTextInput ? (
					<>
						<textarea
							ref={messageRef}
							placeholder={"Mensaje"}
							disabled={disabled}
							value={formData.message}
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
								setContactWarning(null);
								setFormData({ ...formData, message: e.target.value });
							}}
							onBlur={e => handleBlur(e, "message")}
						></textarea>
					</>
				) : null}

				{/* {country_code !== "ce3" && country_code !== "CO" && !isProject ? (
					<div className="container-busca-por-ti">
						<CheckboxCustomInput
							value="first_checkbox"
							id="checkbox-busca-por-ti"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setFormData({ ...formData, busca_por_ti: e.target.checked })
							}
							name="buscapormi"
						>
							<span>Quiero que {site_name} busque por mí</span>


							<button onClick={e => handleShowModal(e, true)} className="info-btn">
								<QuestionIcon width={20} height={20} />
							</button>
						</CheckboxCustomInput>
					</div>
				) : null} */}

				{isFincaraizColombia && !user?.data?.me?.is_terms_and_conditions_accepted ? (
					<>
						<CheckboxCustomInput
							className="tycfinca"
							value="acceptTYC"
							id="checkboxFincaTyC"
							onChange={handleFincaTYC}
							hasError={false}
							name="tyc"
							checked={formData.fincatyc}
						>
							Aceptas nuestros{" "}
							<a href="/informacion#terminos-y-condiciones" target="_blank">
								Términos y condiciones
							</a>{" "}
							y{" "}
							<a href="/informacion#politicas-de-privacidad" target="_blank">
								Política de privacidad
							</a>
						</CheckboxCustomInput>
						{errors.fincatyc && renderWarningMessage("Obligatorio aceptar términos y condiciones")}
					</>
				) : null}

				<LeadButtons
					id={id}
					disabled={disabled}
					setDataModal={setFormData}
					queryRouter={queryRouter}
					forceState={modalState.contactLead}
					hasAcceptedTerms={!isFincaraizColombia || user?.data?.me?.is_terms_and_conditions_accepted || formData.fincatyc}
					onTermsError={(e) => {
						e.preventDefault();
						e.stopPropagation();

						setErrors({ ...errors, fincatyc: true })
					}}
					onBeforeAction={handleLeadButtonValidation}
					onWpClick={() => {
						setModalState(prevState => ({
							...prevState,
							similarModal: ModalState.OPEN,
							contactLead: ModalState.CLOSE,
						}));
					}}  //Evaluar eliminarlo si se usa pero el de tlf no se usa
					owner={owner}
					isProject={isProject}
					source={source}
					propertyLink={propertyLink}
					ctaToShow={ctaToShow}
				>
					<button
						disabled={disabled}
						className={`btn-primary ${isFromModal ? "btn-modal" : ""}`}
						onClick={(e) => submitForm(e)}
					>
						{isLoading ? (
							<LoadingIcon classes="loading-icon" width={16} />
						) : (
							<MessageIcon />
						)}
						{isLoading ? "Enviando" : contactText}
					</button>
				</LeadButtons>
				{isFincaraizColombia && contactWarning ? renderWarningMessage(
					`Completa el formulario para ver ${contactWarning === "whatsapp" ? "WhatsApp" : "teléfono"}`,
					"contact-warning"
				) : null}
			</form>

			{
				country_code !== "CO" &&
				(source === "propertyDetailMain" || modalData?.source === "propertyDetailMain") ?
				<div className="property-express-questions">
					{country_code !== "ce3" ?
						<button
							className="btn-express"
							onClick={() => handleExpressClick(1)}
							disabled={
								disabled ||
								isSendedConsultation.lowerPrice
							}
						>
							<span className="icon"><DollarOutlined width={14} /></span>
							Avísame cuando baje de precio
						</button>
					: null }

					<button
						className="btn-express"
						onClick={() => handleExpressClick(2)}
						disabled={
							disabled || 
							isSendedConsultation.wantedCall
						}
					>
						<span className="icon"><PhoneOutlined width={14} /></span>
						Quiero que me llamen
					</button>

					{country_code !== "ce3" ?
						<button
							className="btn-express"
							onClick={() => handleExpressClick(3)}
							disabled={
								disabled ||
								isSendedConsultation.scheduleVisit
							}
						>
							<span className="icon"><CalendarOutlined width={14} /></span>
							Agendar Visita
						</button>
					: null }
				</div>
			: null }
		</>
	);
};
